import React, { useState, useEffect } from 'react';
import { X, Save, RefreshCw, AlertTriangle, Check, ChevronRight, FileText, Plus } from 'lucide-react';

const PromptManagementModal = ({ isOpen, onClose, apiUrl }) => {
    const [prompts, setPrompts] = useState({});
    const [selectedKey, setSelectedKey] = useState(null);
    const [selectedSubKey, setSelectedSubKey] = useState(null); // For nested prompts like TYPE_SPECIFIC_PROMPTS
    const [editValue, setEditValue] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null); // { type: 'success' | 'error', text: '' }

    // Add Prompt State
    const [showAddModal, setShowAddModal] = useState(false);
    const [newPromptKey, setNewPromptKey] = useState('');
    const [newPromptParent, setNewPromptParent] = useState('');

    // Fetch prompts on open
    useEffect(() => {
        if (isOpen) {
            fetchPrompts();
        }
    }, [isOpen]);

    const fetchPrompts = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${apiUrl}/api/admin/prompts`);
            if (!response.ok) throw new Error('Failed to fetch prompts');
            const data = await response.json();
            setPrompts(data);

            // Select first prompt by default if nothing selected
            if (!selectedKey && Object.keys(data).length > 0) {
                const firstKey = Object.keys(data)[0];
                setSelectedKey(firstKey);
                if (typeof data[firstKey] === 'object' && data[firstKey] !== null) {
                    setSelectedSubKey(Object.keys(data[firstKey])[0]);
                } else {
                    setSelectedSubKey(null);
                }
            }
        } catch (error) {
            console.error('Error fetching prompts:', error);
            setMessage({ type: 'error', text: '프롬프트 로드 실패: ' + error.message });
        } finally {
            setLoading(false);
        }
    };

    // Update edit value when selection changes
    useEffect(() => {
        if (!selectedKey || !prompts[selectedKey]) return;

        let value = prompts[selectedKey];
        if (selectedSubKey && typeof value === 'object') {
            value = value[selectedSubKey];
        }

        // Ensure value is string
        if (typeof value === 'object') {
            setEditValue(JSON.stringify(value, null, 2));
        } else {
            setEditValue(value || '');
        }
    }, [selectedKey, selectedSubKey, prompts]);

    const handleSave = async () => {
        setSaving(true);
        setMessage(null);
        try {
            const payload = {
                key: selectedSubKey || selectedKey,
                value: editValue,
                parentKey: selectedSubKey ? selectedKey : null
            };

            const response = await fetch(`${apiUrl}/api/admin/prompts`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) throw new Error('Failed to save prompt');

            setMessage({ type: 'success', text: '저장되었습니다.' });

            // Update local state
            const newPrompts = { ...prompts };
            if (selectedSubKey) {
                newPrompts[selectedKey][selectedSubKey] = editValue;
            } else {
                newPrompts[selectedKey] = editValue;
            }
            setPrompts(newPrompts);

            // Clear success message after 3 seconds
            setTimeout(() => setMessage(null), 3000);
        } catch (error) {
            console.error('Error saving prompt:', error);
            setMessage({ type: 'error', text: '저장 실패: ' + error.message });
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    // Helper to render prompt list item
    const renderPromptItem = (key, label, isNested = false) => {
        const isSelected = selectedKey === key && !selectedSubKey;

        return (
            <div key={key}>
                <div
                    className={`flex items-center px-4 py-3 cursor-pointer hover:bg-gray-100 transition-colors ${isSelected ? 'bg-blue-50 border-l-4 border-blue-500' : ''}`}
                    onClick={() => {
                        setSelectedKey(key);
                        setSelectedSubKey(null);
                    }}
                >
                    {isNested ? <FileText size={16} className="mr-2 text-gray-500" /> : <FileText size={18} className="mr-2 text-gray-600" />}
                    <span className={`text-sm font-medium ${isSelected ? 'text-blue-700' : 'text-gray-700'}`}>
                        {label || key}
                    </span>
                    {prompts[key] && typeof prompts[key] === 'object' && (
                        <ChevronRight size={16} className="ml-auto text-gray-400" />
                    )}
                </div>

                {/* Render nested items if this is an object (e.g. TYPE_SPECIFIC_PROMPTS) */}
                {prompts[key] && typeof prompts[key] === 'object' && (
                    <div className="bg-gray-50 border-b border-gray-200">
                        {Object.keys(prompts[key]).map(subKey => (
                            <div
                                key={`${key}-${subKey}`}
                                className={`flex items-center pl-10 pr-4 py-2 cursor-pointer hover:bg-gray-200 transition-colors ${selectedKey === key && selectedSubKey === subKey ? 'bg-blue-100 text-blue-800' : 'text-gray-600'}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedKey(key);
                                    setSelectedSubKey(subKey);
                                }}
                            >
                                <span className="text-xs">• {subKey}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };



    const handleAddPrompt = async () => {
        if (!newPromptKey.trim()) {
            setMessage({ type: 'error', text: '프롬프트 키를 입력해주세요.' });
            return;
        }

        setSaving(true);
        try {
            const payload = {
                key: newPromptKey.trim(),
                value: '새로운 프롬프트 내용을 입력하세요.',
                parentKey: newPromptParent || null
            };

            const response = await fetch(`${apiUrl}/api/admin/prompts`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) throw new Error('Failed to create prompt');

            setMessage({ type: 'success', text: '새 프롬프트가 생성되었습니다.' });
            setShowAddModal(false);
            setNewPromptKey('');
            setNewPromptParent('');

            // Refresh and select
            await fetchPrompts();
            setSelectedKey(newPromptParent || newPromptKey);
            if (newPromptParent) {
                setSelectedSubKey(newPromptKey);
            } else {
                setSelectedSubKey(null);
            }

        } catch (error) {
            console.error('Error creating prompt:', error);
            setMessage({ type: 'error', text: '생성 실패: ' + error.message });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-[90vw] h-[85vh] flex flex-col overflow-hidden relative">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <div className="flex items-center space-x-2">
                        <div className="p-2 bg-blue-100 rounded-lg">
                            <FileText className="text-blue-600" size={20} />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-800">시스템 프롬프트 관리자</h2>
                            <p className="text-xs text-gray-500">전역 시스템 프롬프트를 수정합니다. (개별 문서 템플릿은 '템플릿 관리'에서 설정)</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full transition-colors"
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Content */}
                <div className="flex flex-1 overflow-hidden">
                    {/* Sidebar */}
                    <div className="w-1/4 border-r border-gray-200 overflow-y-auto bg-gray-50 flex flex-col">
                        <div className="p-3 border-b border-gray-200 flex justify-between items-center bg-white sticky top-0 z-10">
                            <span className="text-xs font-semibold text-gray-500">프롬프트 목록</span>
                            <button
                                onClick={() => setShowAddModal(true)}
                                className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                title="새 프롬프트 추가"
                            >
                                <Plus size={16} />
                            </button>
                        </div>
                        {loading ? (
                            <div className="flex items-center justify-center h-full text-gray-500">
                                <RefreshCw className="animate-spin mr-2" size={20} />
                                로딩 중...
                            </div>
                        ) : (
                            <div className="py-2">
                                {Object.keys(prompts)
                                    .filter(key => !['TYPE_SPECIFIC_PROMPTS', 'TYPE_SPECIFIC_PROMPTS_V3'].includes(key)) // ⭐ 문서별 프롬프트 제외
                                    .map(key => renderPromptItem(key, key))}

                                {/* 시스템/레거시 섹션 (접을 수 있게 하거나 별도 표시) */}
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                    <p className="px-4 text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">System / Legacy</p>
                                    {Object.keys(prompts)
                                        .filter(key => ['TYPE_SPECIFIC_PROMPTS', 'TYPE_SPECIFIC_PROMPTS_V3'].includes(key))
                                        .map(key => renderPromptItem(key, key, true))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Main Editor Area */}
                    <div className="flex-1 flex flex-col bg-white">
                        {/* Toolbar */}
                        <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200">
                            <div className="flex items-center space-x-2">
                                <span className="text-sm font-semibold text-gray-700">
                                    {selectedKey} {selectedSubKey ? `> ${selectedSubKey}` : ''}
                                </span>
                                {message && (
                                    <span className={`text-xs px-2 py-1 rounded-full flex items-center ${message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                        {message.type === 'success' ? <Check size={12} className="mr-1" /> : <AlertTriangle size={12} className="mr-1" />}
                                        {message.text}
                                    </span>
                                )}
                            </div>
                            <div className="flex space-x-2">
                                <button
                                    onClick={fetchPrompts}
                                    className="flex items-center px-3 py-1.5 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                                    title="새로고침"
                                >
                                    <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={saving}
                                    className={`flex items-center px-4 py-1.5 text-sm font-medium text-white rounded-md transition-colors shadow-sm ${saving ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
                                >
                                    <Save size={16} className="mr-2" />
                                    {saving ? '저장 중...' : '저장'}
                                </button>
                            </div>
                        </div>

                        {/* Editor */}
                        <div className="flex-1 p-0 relative">
                            <textarea
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                className="w-full h-full p-6 font-mono text-sm text-gray-800 bg-gray-50 resize-none focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                                spellCheck="false"
                                placeholder="프롬프트 내용을 입력하세요..."
                            />
                        </div>

                        {/* Footer Info */}
                        <div className="px-6 py-2 bg-gray-100 border-t border-gray-200 text-xs text-gray-500 flex justify-between">
                            <span>Lines: {editValue.split('\n').length}</span>
                            <span>Characters: {editValue.length}</span>
                        </div>
                    </div>
                </div>

                {/* Add Prompt Modal */}
                {showAddModal && (
                    <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg shadow-xl p-6 w-96 border border-gray-200">
                            <h3 className="text-lg font-bold mb-4 text-gray-800">새 프롬프트 추가</h3>

                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">상위 카테고리</label>
                                <select
                                    className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                    value={newPromptParent}
                                    onChange={(e) => setNewPromptParent(e.target.value)}
                                >
                                    <option value="">(최상위)</option>
                                    <option value="TYPE_SPECIFIC_PROMPTS">TYPE_SPECIFIC_PROMPTS (Gemini 2.5)</option>
                                    <option value="TYPE_SPECIFIC_PROMPTS_V3">TYPE_SPECIFIC_PROMPTS_V3 (Gemini 3.0)</option>
                                </select>
                            </div>

                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-1">프롬프트 키 (문서 타입 등)</label>
                                <input
                                    type="text"
                                    className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                    placeholder="예: 자동차등록증_Custom"
                                    value={newPromptKey}
                                    onChange={(e) => setNewPromptKey(e.target.value)}
                                />
                            </div>

                            <div className="flex justify-end gap-2">
                                <button
                                    onClick={() => setShowAddModal(false)}
                                    className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                                >
                                    취소
                                </button>
                                <button
                                    onClick={handleAddPrompt}
                                    className="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                                >
                                    생성
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PromptManagementModal;
